import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';

const PayPalEventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDetail, setEventDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(50);
  const [filter, setFilter] = useState('all'); // all, subscription, payment, errors

  useEffect(() => {
    fetchEvents();
  }, [limit]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/super-admin/paypal/webhook-events?limit=${limit}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setEvents(response.data.events || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch webhook events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEventDetail = async (eventId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/super-admin/paypal/webhook-events/${eventId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setEventDetail(response.data);
    } catch (error) {
      console.error('Failed to fetch event detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event.event_id);
    fetchEventDetail(event.event_id);
  };

  const getEventTypeColor = (eventType) => {
    if (eventType.includes('ACTIVATED') || eventType.includes('COMPLETED') || eventType.includes('RE-ACTIVATED')) {
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    }
    if (eventType.includes('CANCELLED') || eventType.includes('DENIED') || eventType.includes('REFUNDED')) {
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    }
    if (eventType.includes('SUSPENDED') || eventType.includes('EXPIRED')) {
      return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    }
    if (eventType.includes('UPDATED')) {
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  const getStatusBadge = (event) => {
    if (event.error) {
      return <span className="px-2 py-0.5 rounded text-xs bg-red-500/20 text-red-400">Error</span>;
    }
    if (event.result?.status === 'processed') {
      return <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400">Processed</span>;
    }
    if (event.result?.status === 'warning') {
      return <span className="px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400">Warning</span>;
    }
    if (event.result?.status === 'duplicate') {
      return <span className="px-2 py-0.5 rounded text-xs bg-gray-500/20 text-gray-400">Duplicate</span>;
    }
    if (event.result?.status === 'ignored') {
      return <span className="px-2 py-0.5 rounded text-xs bg-gray-500/20 text-gray-400">Ignored</span>;
    }
    if (!event.processed) {
      return <span className="px-2 py-0.5 rounded text-xs bg-amber-500/20 text-amber-400">Pending</span>;
    }
    return <span className="px-2 py-0.5 rounded text-xs bg-gray-500/20 text-gray-400">Unknown</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getModeBadge = (mode) => {
    if (mode === 'live') {
      return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-500/20 text-green-500 border border-green-500/30">LIVE</span>;
    }
    if (mode === 'sandbox') {
      return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-500 border border-amber-500/30">SANDBOX</span>;
    }
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-gray-500/20 text-gray-400 border border-gray-500/30">UNKNOWN</span>;
  };

  const filteredEvents = events.filter(event => {
    if (filter === 'all') return true;
    if (filter === 'subscription') return event.event_type.includes('SUBSCRIPTION');
    if (filter === 'payment') return event.event_type.includes('PAYMENT');
    if (filter === 'errors') return event.error || event.result?.status === 'warning';
    if (filter === 'sandbox') return event.paypal_mode === 'sandbox';
    if (filter === 'live') return event.paypal_mode === 'live';
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">PayPal Events</h1>
              <p className="text-purple-200 text-sm">Webhook events received from PayPal</p>
            </div>
          </div>
          <button
            onClick={fetchEvents}
            disabled={loading}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
          >
            <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Events</p>
              <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
            </div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Subscription Events</p>
              <p className="text-2xl font-bold text-green-600">
                {events.filter(e => e.event_type.includes('SUBSCRIPTION')).length}
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Payment Events</p>
              <p className="text-2xl font-bold text-purple-600">
                {events.filter(e => e.event_type.includes('PAYMENT')).length}
              </p>
            </div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Errors/Warnings</p>
              <p className="text-2xl font-bold text-amber-600">
                {events.filter(e => e.error || e.result?.status === 'warning').length}
              </p>
            </div>
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Filter:</span>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'all', label: 'All Events' },
              { key: 'subscription', label: 'Subscription' },
              { key: 'payment', label: 'Payment' },
              { key: 'errors', label: 'Errors/Warnings' }
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === f.key
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="h-6 w-px bg-gray-300"></div>
          <span className="text-sm font-medium text-gray-700">Mode:</span>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter(filter === 'sandbox' ? 'all' : 'sandbox')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'sandbox'
                  ? 'bg-amber-500 text-white'
                  : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              }`}
            >
              Sandbox
            </button>
            <button
              onClick={() => setFilter(filter === 'live' ? 'all' : 'live')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'live'
                  ? 'bg-green-500 text-white'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              Live
            </button>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-sm text-gray-500">Show:</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>
        </div>
      </div>

      {/* Events List and Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Events List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Recent Events</h2>
            <p className="text-xs text-gray-500">Showing {filteredEvents.length} of {totalCount} events</p>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-gray-500">Loading events...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="p-8 text-center">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-gray-500">No webhook events found</p>
              <p className="text-sm text-gray-400 mt-1">Events will appear here when PayPal sends webhooks</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
              {filteredEvents.map((event) => (
                <div
                  key={event.event_id}
                  onClick={() => handleEventClick(event)}
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedEvent === event.event_id
                      ? 'bg-indigo-50 border-l-4 border-indigo-500'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getEventTypeColor(event.event_type)}`}>
                        {event.event_type.replace('BILLING.', '').replace('PAYMENT.', '')}
                      </span>
                      {getModeBadge(event.paypal_mode)}
                    </div>
                    {getStatusBadge(event)}
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p><span className="font-medium">ID:</span> {event.event_id}</p>
                    <p><span className="font-medium">Resource:</span> {event.resource_id || 'N/A'}</p>
                    <p><span className="font-medium">Received:</span> {formatDate(event.received_at)}</p>
                  </div>
                  {event.result?.message && (
                    <p className="text-xs text-amber-600 mt-2 bg-amber-50 px-2 py-1 rounded">
                      {event.result.message}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Event Detail */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Event Details</h2>
            <p className="text-xs text-gray-500">Click an event to view full details</p>
          </div>

          {loadingDetail ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-gray-500">Loading details...</p>
            </div>
          ) : !eventDetail ? (
            <div className="p-8 text-center">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
              <p className="text-gray-500">Select an event to view details</p>
            </div>
          ) : (
            <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
              {/* Event Summary */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded text-sm font-medium border ${getEventTypeColor(eventDetail.event_type)}`}>
                      {eventDetail.event_type}
                    </span>
                    {getModeBadge(eventDetail.paypal_mode)}
                  </div>
                  {getStatusBadge(eventDetail)}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500">Event ID</p>
                    <p className="font-mono text-xs break-all">{eventDetail.event_id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Resource ID</p>
                    <p className="font-mono text-xs break-all">{eventDetail.resource_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">PayPal Mode</p>
                    <p className="text-xs font-medium">
                      {eventDetail.paypal_mode === 'live' ? (
                        <span className="text-green-600">Live (Production)</span>
                      ) : eventDetail.paypal_mode === 'sandbox' ? (
                        <span className="text-amber-600">Sandbox (Testing)</span>
                      ) : (
                        <span className="text-gray-400">Unknown</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Received</p>
                    <p className="text-xs">{formatDate(eventDetail.received_at)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Processed</p>
                    <p className="text-xs">{formatDate(eventDetail.processed_at)}</p>
                  </div>
                </div>
              </div>

              {/* Processing Result */}
              {eventDetail.result && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Processing Result</h3>
                  <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto">
                    {JSON.stringify(eventDetail.result, null, 2)}
                  </pre>
                </div>
              )}

              {/* Error Info */}
              {eventDetail.error && (
                <div>
                  <h3 className="text-sm font-semibold text-red-700 mb-2">Error</h3>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                    {eventDetail.error}
                  </div>
                </div>
              )}

              {/* Full Payload */}
              {eventDetail.payload && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Full PayPal Payload</h3>
                  <pre className="bg-gray-900 text-gray-300 p-4 rounded-lg text-xs overflow-x-auto max-h-[300px]">
                    {JSON.stringify(eventDetail.payload, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">About PayPal Webhooks</p>
            <ul className="list-disc ml-4 space-y-1">
              <li><strong>Subscription events</strong> track subscription lifecycle (activated, cancelled, suspended, etc.)</li>
              <li><strong>Payment events</strong> track recurring payments (completed, denied, refunded)</li>
              <li><strong>Warnings</strong> usually mean the event was received but the organisation wasn&apos;t found in our database</li>
              <li>Events are automatically deduplicated - duplicate webhook deliveries are ignored</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayPalEventsPage;
